"use client";

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import {
  AUTH_MOUNT_ID,
  getSessionToken,
  goToPiAppIfSignedIn,
  openAuthModal,
} from "@/lib/pw-auth";
import { piEvents } from "@/lib/analytics";
import type { PiLoginEntryPoint } from "@/lib/analytics/schema";

/** Identifies the CTA that opened the modal, so the login events can be attributed to it. */
export type LoginContext = {
  entryPoint: PiLoginEntryPoint;
  selectedPlan?: string;
};

type AuthContextValue = {
  /** Every CTA's click handler: go to /pi if signed in, otherwise open login (which itself goes to /pi on success). */
  goToPiApp: (
    checkoutUrl?: string,
    preselectPlan?: boolean,
    loginContext?: LoginContext,
  ) => Promise<void>;
  /** False until the session cookie is read on the client, so server and first client render match. */
  isSignedIn: boolean;
  /** False until that first client-side cookie read happens; use it to hold auth-dependent UI steady. */
  isAuthResolved: boolean;
};

const AuthContext = createContext<AuthContextValue | null>(null);

const sessionListeners = new Set<() => void>();

function subscribeToSession(onChange: () => void) {
  sessionListeners.add(onChange);
  return () => {
    sessionListeners.delete(onChange);
  };
}

/** The SDK writes the cookie outside React, so consumers have to be told to re-read it. */
function emitSessionChange() {
  sessionListeners.forEach((listener) => listener());
}

const getIsSignedIn = () => Boolean(getSessionToken());
// The cookie is unreadable while prerendering, so both values start out as the logged-out case.
const getServerSnapshot = () => false;
const getIsAuthResolved = () => true;

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used inside <AuthProvider>.");
  }

  return context;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const isSignedIn = useSyncExternalStore(
    subscribeToSession,
    getIsSignedIn,
    getServerSnapshot,
  );
  const isAuthResolved = useSyncExternalStore(
    subscribeToSession,
    getIsAuthResolved,
    getServerSnapshot,
  );

  const goToPiApp = useCallback(
    async (
      checkoutUrl?: string,
      preselectPlan = true,
      loginContext?: LoginContext,
    ) => {
      if (await goToPiAppIfSignedIn(checkoutUrl, preselectPlan)) {
        return;
      }

      if (loginContext) {
        piEvents.loginAction({
          action: "modal_viewed",
          loginEntryPoint: loginContext.entryPoint,
          selectedPlan: loginContext.selectedPlan,
        });
      }

      try {
        await openAuthModal(() => {
          const signedIn = Boolean(getSessionToken());
          emitSessionChange();

          if (loginContext) {
            piEvents.loginAction({
              action: signedIn ? "login_succeeded" : "modal_closed",
              loginEntryPoint: loginContext.entryPoint,
              step: signedIn ? "otp" : undefined,
              selectedPlan: loginContext.selectedPlan,
            });
          }

          void goToPiAppIfSignedIn(checkoutUrl, preselectPlan);
        }, checkoutUrl);
      } catch (error) {
        console.error("[auth] Unable to open PW auth", error);
      }
    },
    [],
  );

  const value = useMemo<AuthContextValue>(
    () => ({ goToPiApp, isSignedIn, isAuthResolved }),
    [goToPiApp, isSignedIn, isAuthResolved],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
      <div id={AUTH_MOUNT_ID} />
    </AuthContext.Provider>
  );
}
